(() => {
  'use strict';

  const MOBILE_QUERY = window.matchMedia('(max-width: 749px)');
  const galleryInstances = new WeakMap();
  const modalInstances = new WeakMap();
  const descriptionInstances = new WeakMap();
  const detailInstances = new WeakMap();
  const CONTENT_MOTION_DURATION = 780;
  const CONTENT_MOTION_EASING = 'cubic-bezier(0.45, 0, 0.55, 1)';

  class JiadaDescriptionToggle {
    constructor(frame) {
      this.frame = frame;
      this.copy = frame.querySelector('[data-jiada-description-copy]');
      this.button = frame.querySelector('[data-jiada-description-toggle]');
      this.label = frame.querySelector('[data-jiada-description-label]');
      this.wordLimit = Number.parseInt(frame.dataset.wordLimit, 10) || 45;
      this.expanded = false;
      this.resizeRaf = null;
      this.collapseTimer = null;

      if (!this.copy || !this.button || !this.label) return;

      this.fullMarkup = this.copy.innerHTML;
      this.collapsedMarkup = this.createCollapsedMarkup();
      this.measure = this.measure.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
      this.button.addEventListener('click', () => this.toggle());
      this.copy.addEventListener('transitionend', this.handleTransitionEnd);
      window.addEventListener('resize', this.handleResize, { passive: true });
      requestAnimationFrame(this.measure);
    }

    handleTransitionEnd(event) {
      if (event.propertyName !== 'max-height' || this.expanded) return;
      window.clearTimeout(this.collapseTimer);
      this.copy.innerHTML = this.collapsedMarkup;
    }

    createCollapsedMarkup() {
      const source = document.createElement('div');
      source.innerHTML = this.fullMarkup;
      const totalWords = (source.textContent.match(/\S+/g) || []).length;
      if (totalWords <= this.wordLimit) return null;

      const walker = document.createTreeWalker(source, NodeFilter.SHOW_TEXT);
      let wordsSeen = 0;
      let textNode = walker.nextNode();

      while (textNode) {
        const words = Array.from(textNode.textContent.matchAll(/\S+/g));
        if (wordsSeen + words.length >= this.wordLimit) {
          const lastWord = words[this.wordLimit - wordsSeen - 1];
          const endOffset = lastWord.index + lastWord[0].length;
          const range = document.createRange();
          range.setStart(textNode, endOffset);
          range.setEnd(source, source.childNodes.length);
          range.deleteContents();
          textNode.textContent = `${textNode.textContent.trimEnd()}...`;
          break;
        }

        wordsSeen += words.length;
        textNode = walker.nextNode();
      }

      Array.from(source.querySelectorAll('p, li, ul, ol, div')).reverse().forEach((element) => {
        if (!element.textContent.trim() && !element.querySelector('img, video, iframe')) element.remove();
      });

      return source.innerHTML;
    }

    measureMarkup(markup) {
      const clone = this.copy.cloneNode(false);
      clone.innerHTML = markup;
      clone.style.position = 'absolute';
      clone.style.width = `${this.copy.getBoundingClientRect().width}px`;
      clone.style.maxHeight = 'none';
      clone.style.height = 'auto';
      clone.style.overflow = 'visible';
      clone.style.visibility = 'hidden';
      clone.style.pointerEvents = 'none';
      clone.style.transition = 'none';
      this.copy.parentElement.appendChild(clone);
      const height = clone.scrollHeight;
      clone.remove();
      return height;
    }

    handleResize() {
      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = requestAnimationFrame(() => {
        this.resizeRaf = null;
        this.measure();
      });
    }

    measure() {
      const needsToggle = Boolean(this.collapsedMarkup);
      const fullHeight = this.measureMarkup(this.fullMarkup);
      const collapsedHeight = needsToggle ? this.measureMarkup(this.collapsedMarkup) : fullHeight;
      this.frame.style.setProperty('--jiada-description-collapsed-height', `${collapsedHeight}px`);
      this.frame.style.setProperty('--jiada-description-full-height', `${fullHeight}px`);
      this.frame.classList.toggle('is-collapsible', needsToggle);
      this.button.hidden = !needsToggle;

      if (!needsToggle) {
        this.expanded = false;
        this.frame.classList.remove('is-expanded');
        this.button.setAttribute('aria-expanded', 'false');
        this.label.textContent = 'Read more';
        this.copy.innerHTML = this.fullMarkup;
      } else if (this.expanded) {
        this.copy.innerHTML = this.fullMarkup;
      } else {
        this.copy.innerHTML = this.collapsedMarkup;
      }
    }

    toggle() {
      if (!this.collapsedMarkup) return;
      window.clearTimeout(this.collapseTimer);
      this.expanded = !this.expanded;

      if (this.expanded) this.copy.innerHTML = this.fullMarkup;

      this.frame.classList.toggle('is-expanded', this.expanded);
      this.button.setAttribute('aria-expanded', this.expanded ? 'true' : 'false');
      this.label.textContent = this.expanded ? 'Read less' : 'Read more';

      if (!this.expanded) {
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reducedMotion) {
          this.copy.innerHTML = this.collapsedMarkup;
          return;
        }

        this.collapseTimer = window.setTimeout(() => {
          if (!this.expanded) this.copy.innerHTML = this.collapsedMarkup;
        }, CONTENT_MOTION_DURATION + 120);
      }
    }
  }

  class JiadaDetailsToggle {
    constructor(details) {
      this.details = details;
      this.summary = details.querySelector(':scope > summary');
      this.content = details.querySelector(':scope > .jiada-product-detail__content');
      this.animation = null;

      if (!this.summary || !this.content) return;

      this.details.classList.toggle('is-expanded', this.details.open);
      this.summary.addEventListener('click', (event) => {
        event.preventDefault();
        this.toggle();
      });
    }

    toggle() {
      const shouldOpen = !this.details.classList.contains('is-expanded');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const currentHeight = this.details.open ? this.content.getBoundingClientRect().height : 0;
      const currentOpacity = this.details.open
        ? Number.parseFloat(window.getComputedStyle(this.content).opacity) || 1
        : 0;

      if (this.animation) this.animation.cancel();

      if (reducedMotion) {
        this.details.open = shouldOpen;
        this.details.classList.toggle('is-expanded', shouldOpen);
        return;
      }

      if (shouldOpen) this.details.open = true;
      this.details.classList.toggle('is-expanded', shouldOpen);

      const endHeight = shouldOpen ? this.content.scrollHeight : 0;
      const endOpacity = shouldOpen ? 1 : 0;

      this.content.style.overflow = 'hidden';
      const animation = this.content.animate(
        [
          { height: `${currentHeight}px`, opacity: currentOpacity },
          { height: `${endHeight}px`, opacity: endOpacity },
        ],
        {
          duration: CONTENT_MOTION_DURATION,
          easing: CONTENT_MOTION_EASING,
        }
      );
      this.animation = animation;

      animation.onfinish = () => {
        if (this.animation !== animation) return;
        if (!shouldOpen) this.details.open = false;
        this.content.style.removeProperty('overflow');
        this.animation = null;
      };

      animation.oncancel = () => {
        if (this.animation !== animation) return;
        this.content.style.removeProperty('overflow');
        this.animation = null;
      };
    }
  }

  class JiadaMediaRail {
    constructor(gallery) {
      this.gallery = gallery;
      this.viewer = gallery.querySelector('[id^="GalleryViewer-"]');
      this.list = gallery.querySelector('[id^="Slider-Gallery-"]');
      this.dots = null;
      this.items = [];
      this.raf = null;

      if (!this.viewer || !this.list) return;

      this.handleScroll = this.handleScroll.bind(this);
      this.rebuild = this.rebuild.bind(this);
      this.list.addEventListener('scroll', this.handleScroll, { passive: true });
      this.observer = new MutationObserver(this.rebuild);
      this.observer.observe(this.list, { childList: true });
      this.rebuild();
    }

    rebuild() {
      this.items = Array.from(this.list.children).filter((item) =>
        item.classList.contains('product__media-item')
      );

      if (this.dots) this.dots.remove();

      this.dots = document.createElement('div');
      this.dots.className = 'jiada-gallery-dots';
      this.dots.setAttribute('role', 'group');
      this.dots.setAttribute('aria-label', 'Choose product image');
      this.dots.hidden = this.items.length < 2;

      this.items.forEach((item, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'jiada-gallery-dot';
        dot.setAttribute('aria-label', `Show image ${index + 1} of ${this.items.length}`);
        dot.setAttribute('aria-current', index === 0 ? 'true' : 'false');
        dot.addEventListener('click', () => {
          this.list.scrollTo({
            left: item.offsetLeft,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          });
        });
        this.dots.appendChild(dot);
      });

      this.viewer.appendChild(this.dots);
      requestAnimationFrame(() => this.updateActiveDot());
    }

    handleScroll() {
      if (!MOBILE_QUERY.matches || this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this.updateActiveDot();
      });
    }

    updateActiveDot() {
      if (!this.dots || !this.items.length) return;

      let activeIndex = 0;
      let smallestDistance = Number.POSITIVE_INFINITY;

      this.items.forEach((item, index) => {
        const distance = Math.abs(item.offsetLeft - this.list.scrollLeft);
        if (distance < smallestDistance) {
          smallestDistance = distance;
          activeIndex = index;
        }
      });

      Array.from(this.dots.children).forEach((dot, index) => {
        dot.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
      });

      this.items.forEach((item, index) => {
        item.classList.toggle('is-active', index === activeIndex);
      });
    }
  }

  class JiadaProductModal {
    constructor(modal) {
      this.modal = modal;
      this.dialog = modal.querySelector('.product-media-modal__dialog');
      this.content = modal.querySelector('.product-media-modal__content');
      this.items = [];
      this.index = 0;
      this.pointerStart = null;
      this.zoomScales = [1, 1.5, 2.5];
      this.zoomIndex = 0;
      this.panX = 0;
      this.panY = 0;
      this.preloadedImages = [];

      if (!this.dialog || !this.content) return;

      this.previousButton = this.createButton('previous');
      this.nextButton = this.createButton('next');
      this.zoomButton = this.createZoomButton();
      this.thumbnailList = document.createElement('div');
      this.thumbnailList.className = 'jiada-modal-thumbnails';
      this.thumbnailList.setAttribute('role', 'group');
      this.thumbnailList.setAttribute('aria-label', 'Choose image');
      this.dialog.append(this.previousButton, this.nextButton, this.zoomButton, this.thumbnailList);

      this.previousButton.addEventListener('click', () => this.move(-1));
      this.nextButton.addEventListener('click', () => this.move(1));
      this.zoomButton.addEventListener('click', () => this.cycleZoom());
      [this.previousButton, this.nextButton, this.zoomButton].forEach((button) => {
        button.addEventListener('pointerdown', (event) => event.stopPropagation());
        button.addEventListener('pointerup', (event) => event.stopPropagation());
      });
      this.thumbnailList.addEventListener('pointerdown', (event) => event.stopPropagation());
      this.thumbnailList.addEventListener('pointerup', (event) => event.stopPropagation());

      this.modal.addEventListener('keydown', (event) => {
        if (!this.modal.hasAttribute('open')) return;
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.move(-1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.move(1);
        }
      });

      this.content.addEventListener('dragstart', (event) => event.preventDefault());
      this.content.addEventListener('pointerdown', (event) => this.handlePointerDown(event));
      this.content.addEventListener('pointermove', (event) => this.handlePointerMove(event));
      this.content.addEventListener('pointerup', (event) => this.handlePointerUp(event));
      this.content.addEventListener('pointercancel', () => this.endPointerInteraction());

      this.observer = new MutationObserver((records) => {
        if (records.some((record) => record.attributeName === 'open')) {
          if (this.modal.hasAttribute('open')) this.syncFromOpenedMedia();
          else this.resetZoom();
        }
      });
      this.observer.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
      window.addEventListener('resize', () => {
        if (this.modal.hasAttribute('open') && this.zoomIndex > 0) this.applyZoom();
      });
      MOBILE_QUERY.addEventListener('change', () => {
        if (!this.modal.hasAttribute('open')) return;
        this.resetZoom();
        this.applyZoom();
      });
      this.preloadModalImages();
    }

    createButton(direction) {
      const button = document.createElement('button');
      const isPrevious = direction === 'previous';
      button.type = 'button';
      button.className = `jiada-modal-nav jiada-modal-nav--${direction}`;
      button.setAttribute('aria-label', isPrevious ? 'Previous image' : 'Next image');
      button.innerHTML = isPrevious
        ? '<svg viewBox="0 0 24 40" aria-hidden="true"><path d="M20 3 4 20l16 17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square" stroke-linejoin="miter"/></svg>'
        : '<svg viewBox="0 0 24 40" aria-hidden="true"><path d="m4 3 16 17L4 37" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square" stroke-linejoin="miter"/></svg>';
      return button;
    }

    createZoomButton() {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'jiada-modal-zoom';
      button.setAttribute('aria-label', 'Zoom in');
      button.innerHTML = '<span class="jiada-modal-zoom__symbol" aria-hidden="true">+</span>';
      return button;
    }

    activeImage() {
      const activeItem = this.items[this.index];
      if (!activeItem) return null;
      return activeItem.tagName === 'IMG' ? activeItem : activeItem.querySelector('img');
    }

    handlePointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      const image = this.activeImage();
      const isActiveImage = image && event.target === image;
      this.pointerStart = {
        x: event.clientX,
        y: event.clientY,
        originX: this.panX,
        originY: this.panY,
        image: isActiveImage ? image : null,
        pointerId: event.pointerId,
        pointerType: event.pointerType,
        moved: false,
      };

      if (isActiveImage) {
        event.stopPropagation();
        image.setPointerCapture?.(event.pointerId);
        if (this.zoomIndex > 0) image.classList.add('is-dragging');
      }
    }

    handlePointerMove(event) {
      const interaction = this.pointerStart;
      if (!interaction || interaction.pointerId !== event.pointerId || !interaction.image) return;

      const deltaX = event.clientX - interaction.x;
      const deltaY = event.clientY - interaction.y;
      if (Math.abs(deltaX) > 4 || Math.abs(deltaY) > 4) interaction.moved = true;

      if (this.zoomIndex === 0) return;

      event.preventDefault();
      this.panX = interaction.originX + deltaX;
      this.panY = interaction.originY + deltaY;
      this.applyZoom();
    }

    handlePointerUp(event) {
      const interaction = this.pointerStart;
      if (!interaction || interaction.pointerId !== event.pointerId) return;

      const deltaX = event.clientX - interaction.x;
      const deltaY = event.clientY - interaction.y;

      if (interaction.image) {
        event.stopPropagation();
        interaction.image.releasePointerCapture?.(event.pointerId);
        interaction.image.classList.remove('is-dragging');

        if (this.zoomIndex === 0 && interaction.pointerType === 'touch' && Math.abs(deltaX) >= 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
          this.endPointerInteraction();
          this.move(deltaX > 0 ? -1 : 1);
          return;
        }

        if (!interaction.moved && interaction.pointerType === 'mouse') this.cycleZoom();
        this.endPointerInteraction();
        return;
      }

      this.endPointerInteraction();
      if (interaction.pointerType !== 'touch') return;
      if (Math.abs(deltaX) < 50 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      this.move(deltaX > 0 ? -1 : 1);
    }

    endPointerInteraction() {
      if (this.pointerStart?.image) this.pointerStart.image.classList.remove('is-dragging');
      this.pointerStart = null;
    }

    cycleZoom() {
      if (MOBILE_QUERY.matches || !this.activeImage()) return;
      this.zoomIndex = (this.zoomIndex + 1) % this.zoomScales.length;
      this.panX = 0;
      this.panY = 0;
      this.applyZoom();
    }

    updateZoomButton() {
      const image = this.activeImage();
      const isNextActionZoomOut = this.zoomIndex === this.zoomScales.length - 1;
      const enableDesktopCursor = !MOBILE_QUERY.matches && Boolean(image);
      this.content.classList.toggle('jiada-zoom-cursor-in', enableDesktopCursor && !isNextActionZoomOut);
      this.content.classList.toggle('jiada-zoom-cursor-out', enableDesktopCursor && isNextActionZoomOut);
      this.zoomButton.hidden = true;
    }

    resetZoom() {
      this.zoomIndex = 0;
      this.panX = 0;
      this.panY = 0;
      this.allMediaItems().forEach((item) => {
        const image = item.tagName === 'IMG' ? item : item.querySelector('img');
        if (!image) return;
        image.classList.remove('jiada-modal-image--zoomable', 'is-zoomed', 'is-dragging');
        image.style.removeProperty('--jiada-zoom');
        image.style.removeProperty('--jiada-pan-x');
        image.style.removeProperty('--jiada-pan-y');
      });
      this.updateZoomButton();
    }

    applyZoom() {
      const image = this.activeImage();
      if (!image) {
        this.updateZoomButton();
        return;
      }

      const scale = this.zoomScales[this.zoomIndex];
      const bounds = this.content.getBoundingClientRect();
      const maxX = Math.max(0, ((scale - 1) * bounds.width) / 2);
      const maxY = Math.max(0, ((scale - 1) * bounds.height) / 2);
      this.panX = Math.min(maxX, Math.max(-maxX, this.panX));
      this.panY = Math.min(maxY, Math.max(-maxY, this.panY));

      image.classList.add('jiada-modal-image--zoomable');
      image.classList.toggle('is-zoomed', scale > 1);
      image.style.setProperty('--jiada-zoom', scale);
      image.style.setProperty('--jiada-pan-x', `${this.panX}px`);
      image.style.setProperty('--jiada-pan-y', `${this.panY}px`);
      this.updateZoomButton();
    }

    previewImageFor(item) {
      if (item.tagName === 'IMG') return item;
      return item.querySelector('img');
    }

    preloadModalImages() {
      const images = this.allMediaItems()
        .map((item) => this.previewImageFor(item))
        .filter(Boolean);

      const preload = (image, highPriority = false) => {
        image.loading = 'eager';
        image.decoding = 'async';
        if (highPriority) image.fetchPriority = 'high';

        const request = new Image();
        request.decoding = 'async';
        request.fetchPriority = highPriority ? 'high' : 'auto';
        if (image.srcset) request.srcset = image.srcset;
        request.sizes = '100vw';
        request.src = image.currentSrc || image.src;
        this.preloadedImages.push(request);
      };

      if (images[0]) preload(images[0], true);
      const preloadRemaining = () => images.slice(1).forEach((image) => preload(image));

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(preloadRemaining, { timeout: 1500 });
      } else {
        window.setTimeout(preloadRemaining, 250);
      }
    }

    rebuildThumbnails() {
      this.thumbnailList.replaceChildren();
      this.thumbnailList.hidden = this.items.length < 2;

      this.items.forEach((item, index) => {
        const sourceImage = this.previewImageFor(item);
        if (!sourceImage) return;

        const button = document.createElement('button');
        const image = document.createElement('img');
        button.type = 'button';
        button.className = 'jiada-modal-thumbnail';
        button.setAttribute('aria-label', `Show image ${index + 1} of ${this.items.length}`);
        image.src = sourceImage.currentSrc || sourceImage.src;
        image.alt = '';
        image.loading = 'lazy';
        image.draggable = false;
        button.appendChild(image);
        button.addEventListener('pointerenter', () => this.show(index));
        button.addEventListener('click', () => this.show(index));
        this.thumbnailList.appendChild(button);
      });
    }

    updateThumbnails() {
      Array.from(this.thumbnailList.children).forEach((thumbnail, index) => {
        thumbnail.setAttribute('aria-current', index === this.index ? 'true' : 'false');
      });
    }

    allMediaItems() {
      return Array.from(this.content.children).filter((item) => item.hasAttribute('data-media-id'));
    }

    syncFromOpenedMedia() {
      const allItems = this.allMediaItems();
      const openedItem = allItems.find((item) => item.classList.contains('active')) || allItems[0];

      this.items = allItems.filter(
        (item) => !item.classList.contains('product__media-item--variant') || item === openedItem
      );

      if (openedItem && !this.items.includes(openedItem)) this.items.unshift(openedItem);
      this.items.sort((first, second) => {
        if (first === second) return 0;
        return first.compareDocumentPosition(second) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
      });

      this.index = Math.max(0, this.items.indexOf(openedItem));
      this.rebuildThumbnails();
      this.show(this.index);
    }

    show(index) {
      if (!this.items.length) {
        this.previousButton.hidden = true;
        this.nextButton.hidden = true;
        return;
      }

      this.index = (index + this.items.length) % this.items.length;
      const activeItem = this.items[this.index];
      const activeImage = this.previewImageFor(activeItem);
      if (activeImage) {
        activeImage.loading = 'eager';
        activeImage.fetchPriority = 'high';
        if (typeof activeImage.decode === 'function') activeImage.decode().catch(() => {});
      }
      this.resetZoom();

      this.allMediaItems().forEach((item) => {
        const isActive = item === activeItem;
        item.classList.toggle('active', isActive);
        item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      const hideNavigation = this.items.length < 2;
      this.previousButton.hidden = hideNavigation;
      this.nextButton.hidden = hideNavigation;
      this.updateThumbnails();
      this.applyZoom();

      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
    }

    move(step) {
      if (this.items.length < 2) return;
      this.show(this.index + step);
    }
  }

  function enhance(root = document) {
    root.querySelectorAll('[data-jiada-description]').forEach((description) => {
      if (!descriptionInstances.has(description)) {
        descriptionInstances.set(description, new JiadaDescriptionToggle(description));
      }
    });

    root.querySelectorAll('.jiada-product-detail').forEach((details) => {
      if (!detailInstances.has(details)) {
        detailInstances.set(details, new JiadaDetailsToggle(details));
      }
    });

    root.querySelectorAll('media-gallery').forEach((gallery) => {
      if (!galleryInstances.has(gallery)) galleryInstances.set(gallery, new JiadaMediaRail(gallery));
    });

    root.querySelectorAll('product-modal.product-media-modal').forEach((modal) => {
      if (!modalInstances.has(modal)) modalInstances.set(modal, new JiadaProductModal(modal));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => enhance());
  } else {
    enhance();
  }

  document.addEventListener('shopify:section:load', () => enhance(document));
})();
